// Library imports
import React, { useState, useEffect } from 'react';
import { MDBCard, MDBCardBody, MDBCardHeader, MDBBtn, MDBIcon, MDBRow, MDBCol } from 'mdb-react-ui-kit';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
// Component imports
import NoData from '../../../../../components/NoData';
// Styled components
import { InnerContent } from '../../styles';
import {
    EmptyPropsState,
    PlayerHeader,
    PlayerName,
    PlayerPosition,
    PropCardButton,
    PropChip,
    PropMetaRow,
    PropNameText,
    PropTitleRow,
    SectionTitle,
    StatLabel,
    StatsGrid,
    StatTile,
    StatValue,
    PropsList,
} from './styles';
import { formatOdd } from '../../../../../utils/odds';
// API imports
import { getPlayerData } from '../../../../../api/players';

const PlayerData = ({ player, setView, setPlayerWithProps, setSelectedProp, demo }) => {
    // Redux
    const { sport } = useSelector(state => state.app);
    // Local state
    const [loading, setLoading] = useState(false);
    const [playerData, setPlayerData] = useState(player);

    useEffect(() => {
        async function fetchPlayerData() {
            try {
                setLoading(true);
                const playerRes = await getPlayerData(sport, playerData.id);
                setPlayerData(playerRes);
            } catch (error) {
                console.log(`Error fetching player data: ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
        if (player?.id && !demo) {
            fetchPlayerData();
        }
    }, [player?.id]);

    if (demo) {
        return (
            <InnerContent fluid>
                <MDBBtn className="mb-3" color="secondary" onClick={() => setView('roster')}>
                    <MDBIcon fas icon="arrow-left" className="me-2" /> Back to Roster
                </MDBBtn>
                <NoData title="Demo View" message={`This is a demo view. Player data and prop analysis are not available.`} />
            </InnerContent>
        );
    }

    if (!loading && !playerData?.stats) {
        return (
            <InnerContent fluid>
                <MDBBtn className="mb-3" color="secondary" onClick={() => setView('roster')}>
                    <MDBIcon fas icon="arrow-left" className="me-2" /> Back to Roster
                </MDBBtn>
                <NoData title="Player Data" message={`No data found for ${playerData?.name || 'this player'}. Please select another.`} />
            </InnerContent>
        );
    }

    return (
        <InnerContent fluid>
            <MDBCard
                className="mb-3 shadow-2-strong"
                style={{
                    background: 'rgba(15, 23, 42, 0.88)',
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    borderRadius: 14,
                    overflow: 'hidden',
                    color: '#e2e8f0',
                }}
            >
                <MDBCardHeader
                    style={{
                        background: 'linear-gradient(160deg, rgba(30,41,59,0.85) 0%, rgba(15,23,42,0.95) 100%)',
                        borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
                    }}
                >
                    <PlayerHeader>
                        <div>
                            <PlayerName>{loading ? <Skeleton width={120} /> : playerData.name}</PlayerName>
                            <PlayerPosition>{loading ? <Skeleton width={80} /> : playerData.position}</PlayerPosition>
                        </div>
                        <MDBBtn color="secondary" size="sm" onClick={() => setView('roster')}>
                            <MDBIcon fas icon="arrow-left" className="me-2" /> Back
                        </MDBBtn>
                    </PlayerHeader>
                </MDBCardHeader>
                <MDBCardBody style={{ background: 'rgba(15, 23, 42, 0.5)' }}>
                    {loading ? (
                        <MDBRow>
                            <MDBCol md="6" className="mb-3">
                                <Skeleton count={3} />
                            </MDBCol>
                        </MDBRow>
                    ) : (
                        <MDBRow>
                            <MDBCol md="6" className="mb-3">
                                <SectionTitle>
                                    <i className="fas fa-chart-bar" /> General Stats
                                </SectionTitle>
                                <StatsGrid>
                                    {Object.values(playerData.stats).map(stat => (
                                        <StatTile key={stat.id}>
                                            <StatLabel>{stat.label}</StatLabel>
                                            <StatValue>{stat.value}</StatValue>
                                        </StatTile>
                                    ))}
                                </StatsGrid>
                            </MDBCol>
                            <MDBCol md="6" className="mb-3">
                                <SectionTitle>
                                    <i className="fas fa-bolt" /> Player Props
                                </SectionTitle>
                                <PropsList>
                                    {Object.values(playerData.props).filter(prop => prop.value).length > 0 ? (
                                        Object.values(playerData.props)
                                            .filter(prop => prop.value)
                                            .map((prop, i) => (
                                                <PropCardButton
                                                    key={i}
                                                    action
                                                    onClick={() => {
                                                        setPlayerWithProps(playerData);
                                                        setSelectedProp(prop.id);
                                                        setView('prop-insights');
                                                    }}
                                                >
                                                    <PropTitleRow>
                                                        <PropNameText>
                                                            <MDBIcon fas icon="chart-line" />
                                                            {prop.name === 'Anytime Goalscorer' ? 'Anytime Goal' : prop.name}
                                                        </PropNameText>
                                                        <MDBIcon fas icon="angle-right" className="chevron" />
                                                    </PropTitleRow>
                                                    <PropMetaRow>
                                                        <PropChip>
                                                            {prop.type === 'single-value'
                                                                ? formatOdd(prop.value)
                                                                : `O/U ${prop.value}`}
                                                        </PropChip>
                                                        {prop.overOdds != null && (
                                                            <PropChip $tone="over">O {formatOdd(prop.overOdds)}</PropChip>
                                                        )}
                                                        {prop.underOdds != null && (
                                                            <PropChip $tone="under">U {formatOdd(prop.underOdds)}</PropChip>
                                                        )}
                                                    </PropMetaRow>
                                                </PropCardButton>
                                            ))
                                    ) : (
                                        <EmptyPropsState>
                                            No available props to display.
                                        </EmptyPropsState>
                                    )}
                                </PropsList>
                            </MDBCol>
                        </MDBRow>
                    )}
                </MDBCardBody>
            </MDBCard>
        </InnerContent>
    );
};

export default PlayerData;
